import React from 'react'
import { useTranslation } from 'react-i18next'

import Tooltip from '@mui/material/Tooltip'
import Stack from '@mui/material/Stack'
import TableCell from '@mui/material/TableCell'
import Typography from '@mui/material/Typography'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import CircleIcon from '@mui/icons-material/Circle'

import { toDateString, toReadableTimestamp } from '../../utils/dateUtils'
import ProfileTypeChip from './profileTypeChip'

import ResponsiveTableHeader from '../fields/responsiveTableHeader'
import TooltipIconButton from '../fields/tooltipIconButton'
import { Profile } from '../../types/profile'
import { Customer } from '../../types/customer'
import { weightValueToDisplay } from '../../utils/weightUtils'

interface ProfileRowProps {
  profile: Profile
  currentCustomer: Customer | null
  isAssigned: boolean
  assignedMachineSlotNumber: number
  downloadedTimestamp: Date | null | undefined
  canBeAssignedToMachine: boolean
  profileLink: JSX.Element
  createdBy: string
  assignProfile: () => void
  unAssignProfile: () => void
}

const ProfileRow = (props: ProfileRowProps): JSX.Element => {
  const { t } = useTranslation()

  const canAssignProfile = !props.isAssigned
  const canUnassignProfile = props.isAssigned

  return (
    <>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        {!!props.assignedMachineSlotNumber && (
          <Stack direction="row" alignItems="center">
            <ResponsiveTableHeader header={t('profilesview.machineslot')} />
            <Typography>{props.assignedMachineSlotNumber}</Typography>
            <Tooltip
              sx={{ ml: 1 }}
              title={
                props.downloadedTimestamp
                  ? t('profilesview.downloadedtoroaster').replace('{0}', toReadableTimestamp(props.downloadedTimestamp))
                  : t('profilesview.notdownloaded')
              }
            >
              <CircleIcon fontSize="small" color={props.downloadedTimestamp ? 'success' : 'error'} />
            </Tooltip>
          </Stack>
        )}
      </TableCell>
      <TableCell>
        <ResponsiveTableHeader header={t('profilesview.name')} />
        {props.profileLink}
      </TableCell>
      <TableCell>
        <ResponsiveTableHeader header={t('profilesview.createdby')} />
        {props.createdBy}
      </TableCell>
      <TableCell>
        <ResponsiveTableHeader header={t('profilesview.batchweight')} />
        {props.profile.batch_weight ? weightValueToDisplay(props.profile.batch_weight) : ''}
      </TableCell>
      <TableCell>
        <ResponsiveTableHeader header={t('profilesview.profiletype')} />
        <ProfileTypeChip profile={props.profile} />
      </TableCell>
      <TableCell>
        <ResponsiveTableHeader header={t('profilesview.lastmodified')} />
        {toDateString(props.profile.modified)}
      </TableCell>
      <TableCell className="splitter-cell" />
      <TableCell className="action-cell" sx={{ textAlign: 'center' }}>
        {canUnassignProfile && (
          <TooltipIconButton
            onClick={props.unAssignProfile}
            icon={<FileUploadIcon sx={{ transform: 'rotate(180deg)' }} />}
            tooltip={t('profilesview.unassign')}
          />
        )}
        {canAssignProfile && (
          <>
            {props.canBeAssignedToMachine ? (
              <TooltipIconButton
                onClick={props.assignProfile}
                icon={<FileUploadIcon />}
                tooltip={t('profilesview.assign')}
              />
            ) : (
              <Tooltip title={t('profilesview.cannotassigntomachine')}>
                <FileUploadIcon color="disabled" />
              </Tooltip>
            )}
          </>
        )}
      </TableCell>
    </>
  )
}

export default ProfileRow
